import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import Home from './components/Home';
import Contact from './components/Contact';
import Footer from './components/Footer';
import NetworkBackground from './components/NetworkBackground';
import './App.css';

import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import LLMesh from './components/LLMesh';
import Press from './components/Press';
import Solutions from './components/Solutions';
import SolutionDetail from './components/SolutionDetail';
import Company from './components/Company';

// Centralized navigation helpers exposed to Navigation/Footer/Home.
// - navigateTo(target, source?): pushes to /, /contact, /privacy, /terms
// - navigateToAnchor(anchor): if on '/', scrolls smoothly; otherwise navigates
//   to '/' with the anchor in location.state so ScrollManager can complete it.
function useSiteNav() {
    const navigate = useNavigate();
    const location = useLocation();

    const navigateTo = (target, source = null) => {
        if (target === 'home') {
            navigate('/');
        } else if (target === 'contact') {
            const qs = source ? `?source=${encodeURIComponent(source)}` : '';
            navigate(`/contact${qs}`);
        } else if (target === 'privacy' || target === 'terms') {
            navigate(`/${target}`);
        }
    };

    const navigateToAnchor = (anchor) => {
        if (location.pathname === '/') {
            const el = document.getElementById(anchor);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            navigate('/', { state: { anchor } });
        }
    };

    return { navigateTo, navigateToAnchor };
}

// Scroll-to-top on route change (except when an anchor is queued via state).
function ScrollManager() {
    const location = useLocation();
    useEffect(() => {
        if (location.state && location.state.anchor) {
            const id = location.state.anchor;
            // Defer so Home has mounted the target element.
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                else window.scrollTo(0, 0);
            }, 50);
            // Clear state so reloads don't re-trigger.
            window.history.replaceState({}, '');
        } else {
            window.scrollTo(0, 0);
        }
    }, [location]);
    return null;
}

// Pulls ?source=investor off the URL so Contact can render the investor variant.
function ContactWithQuery() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const source = params.get('source');
    return <Contact source={source} />;
}

function AppShell() {
    const { navigateTo, navigateToAnchor } = useSiteNav();

    return (
        <div className="App d-flex flex-column">
            <ScrollManager />
            <NetworkBackground />
            <Navigation setPage={(p) => navigateTo(p)} navigateToAnchor={navigateToAnchor} />
            <Routes>
                <Route path="/" element={<Home setPage={(p, s) => navigateTo(p, s)} />} />
                <Route path="/contact" element={<ContactWithQuery />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/:slug" element={<SolutionDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/llmesh" element={<LLMesh setPage={(p, s) => navigateTo(p, s)} />} />
                <Route path="/company" element={<Company />} />
                <Route path="/company/press" element={<Press />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="*" element={<Home setPage={(p, s) => navigateTo(p, s)} />} />
            </Routes>
            <Footer setPage={(p) => navigateTo(p)} navigateToAnchor={navigateToAnchor} />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppShell />
        </BrowserRouter>
    );
}

export default App;
